/**
 * RosterChartsHorizontalScroll View Component
 *
 * Pure presentation component that renders a horizontal scrollable container
 * with roster analytics charts.
 * @remarks
 * - Displays error state when data fetch fails
 * - Shows empty state when no players exist
 * - Renders two charts side-by-side in horizontal scroll view
 * - Uses fixed chart width for consistent layout
 */

import { ApolloError } from "@apollo/client";
import { SkFont } from "@shopify/react-native-skia";
import { memo } from "react";

import ContractExpirationsByYearChart from "@/components/organisms/ContractExpirationsByYearChart";
import MyRosterByAgeChart from "@/components/organisms/MyRosterByAgeChart";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import { PlayerCardData } from "@/types/playerCardTypes";

const CHART_WIDTH_CLASS = "w-96";

/**
 * Props for RosterChartsHorizontalScroll view
 */
interface Props {
  players: PlayerCardData[];
  font: SkFont | null;
  loading: boolean;
  error: ApolloError | undefined;
  isDark: boolean;
}

/**
 * RosterChartsHorizontalScroll view component
 * @param props - Component props
 * @param props.players - Array of watchlist players
 * @param props.font - Skia font for chart rendering
 * @param props.loading - Loading state indicator
 * @param props.error - Apollo error if data fetch failed
 * @param props.isDark - Dark mode indicator
 */
const RosterChartsHorizontalScrollView = memo<Props>(
  ({ players, font, loading, error, isDark }) =>
    error ? (
      <Stack className="gap-4 py-4">
        <Box className="px-4 py-8">
          <Text variant="body" className="text-status-error">
            Error loading roster data: {error.message}
          </Text>
        </Box>
      </Stack>
    ) : players.length === 0 && !loading ? (
      <Stack className="gap-4 py-4">
        <Box className="rounded-none border border-surface-muted bg-surface-base p-6">
          <Stack className="items-center gap-3">
            <Text variant="body" className="text-center text-content-muted">
              No roster players found
            </Text>
            <Text variant="caption" className="text-center text-content-subtle">
              Add players to your organization to see analytics
            </Text>
          </Stack>
        </Box>
      </Stack>
    ) : (
      <Stack className="gap-2 pb-4 pt-2">
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerClassName="gap-4 pr-5"
        >
          <Box className={CHART_WIDTH_CLASS}>
            <MyRosterByAgeChart
              players={players}
              font={font}
              loading={loading}
              isDark={isDark}
            />
          </Box>
          <Box className={CHART_WIDTH_CLASS}>
            <ContractExpirationsByYearChart
              players={players}
              font={font}
              loading={loading}
              isDark={isDark}
            />
          </Box>
        </ScrollView>
      </Stack>
    )
);

RosterChartsHorizontalScrollView.displayName =
  "RosterChartsHorizontalScrollView";

export default memo(RosterChartsHorizontalScrollView);
